// NFTCard.tsx
import { motion } from 'framer-motion';
import { ExternalLink, User } from 'lucide-react';
import { nextPinataMediaUrl, toPinataMediaUrl } from '../web3/ipfs';

// Optional image props are used by the SFUT Legacy strip (local thumbnail + gateway fallback,
// fixed size, async decode). Without them the card behaves exactly as before (Olympic Series).
const NFTCard = ({
  nft,
  delay = 0,
  collectionAddress = '0xa1d1E588412b998618C1bBc2029eAf5eFF30DE8b',
  ctaLabel = 'Buy',
  imageSrc: imageSrcOverride = null as string | null,
  fallbackImageSrc = null as string | null,
  imageSize = null as number | null,
}) => {
  const imageSrc =
    imageSrcOverride || toPinataMediaUrl(nft.image_url) || toPinataMediaUrl(nft.metadata?.image);
  const truncateAddress = (address) => {
    if (!address) return 'Unknown';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const truncateDescription = (description, maxLength = 80) => {
    if (!description) return '';
    return description.length > maxLength 
      ? `${description.slice(0, maxLength)}...` 
      : description;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -4, transition: { duration: 0.2 } }}
      className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-all duration-300"
    >
      {/* NFT Image */}
      <div className="relative aspect-square bg-gradient-to-br from-blue-50 to-purple-50">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={nft.metadata?.name || `NFT #${nft.id}`}
            className="w-full h-full object-cover"
            {...(imageSize
              ? { width: imageSize, height: imageSize, decoding: 'async', loading: 'lazy' }
              : {})}
            onError={(e) => {
              const img = e.currentTarget as HTMLImageElement;
              if (fallbackImageSrc && !img.dataset.fellBack) {
                img.dataset.fellBack = '1';
                img.src = fallbackImageSrc;
                return;
              }
              const next = nextPinataMediaUrl(e.target.src);
              if (next) {
                e.target.src = next;
                return;
              }
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div className="w-full h-full flex items-center justify-center text-gray-400" style={{ display: imageSrc ? 'none' : 'flex' }}>
          <div className="text-center">
            <div className="w-8 h-8 mx-auto mb-1 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">SF</span>
            </div>
            <p className="text-xs">SportsFuture NFT</p>
          </div>
        </div>
        
        {/* NFT ID Badge */}
        <div className="absolute top-2 left-2 bg-black/70 text-white px-1.5 py-0.5 rounded text-xs font-medium">
          #{nft.id}
        </div>
      </div>

      {/* NFT Details */}
      <div className="p-3">
        <h3 className="font-semibold text-sm mb-1 text-gray-900 line-clamp-1">
          {nft.metadata?.name || `SportsFuture NFT #${nft.id}`}
        </h3>
        
        {nft.metadata?.description && (
          <p className="text-gray-600 text-xs mb-2 leading-tight line-clamp-2">
            {truncateDescription(nft.metadata.description, 80)}
          </p>
        )}

        {/* Owner Info */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <div className="flex items-center space-x-1">
            <User size={12} className="text-gray-500" />
            <span className="text-xs text-gray-600">
              {truncateAddress(nft.owner?.hash)}
            </span>
          </div>
          
          {/* View on OpenPlaza Button */}
          <motion.a
            href={`https://openplaza.io/nft/${collectionAddress}/${nft.id}`}
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-xs font-medium"
          >
            <span>{ctaLabel}</span>
            <ExternalLink size={10} />
          </motion.a>
        </div>
      </div>
    </motion.div>
  );
};

export default NFTCard;
